import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Mode } from "../../types";
import { useInfiniteStorageListQuery } from "../queries/useInfiniteStorageListQuery";
import { useDeleteFromStorageMutation } from "../queries/mutations/useStorageMutations";
import { useGroupListStore } from "../../store";

/**
 * useStorageMenuListView
 *
 * 보관함 목록(튜터 보관함/소크라테스 보관함) 전용 presentation 훅
 *
 * 기능:
 * - 보관함 목록 무한 스크롤 데이터 페칭
 * - 날짜별 섹션 그룹핑
 * - 아이템 클릭/삭제 핸들러 (편집/보관 불가)
 */

interface UseStorageMenuListViewOptions {
  mode: Mode;
}

interface StorageItem {
  id: string;
  label: string;
  date?: string;
  showDate?: boolean;
  isStorage: true;
  isCompleted?: boolean;
}

export const useStorageMenuListView = (
  options: UseStorageMenuListViewOptions
) => {
  const { mode } = options;

  const navigate = useNavigate();
  const { setCurrentGroupId } = useGroupListStore();

  // Infinite Query
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteStorageListQuery({ mode });

  // Mutations
  const deleteFromStorageMutation = useDeleteFromStorageMutation(mode);

  /**
   * 날짜별 그룹핑 및 items 변환
   */
  const storageItems = useMemo(() => {
    if (!data) return [];

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const allStorage = data.pages.flatMap((page) => page.storage);
    const grouped: Record<string, StorageItem[]> = {};

    allStorage.forEach((item) => {
      const itemDate = new Date(item.created_at);
      const itemDateOnly = new Date(
        itemDate.getFullYear(),
        itemDate.getMonth(),
        itemDate.getDate()
      );

      let section: string;

      if (itemDateOnly.getTime() === today.getTime()) {
        section = "오늘";
      } else if (itemDateOnly >= sevenDaysAgo) {
        section = "지난 7일";
      } else {
        const year = itemDate.getFullYear();
        const month = itemDate.getMonth() + 1;
        section = `${year}년 ${month}월`;
      }

      if (!grouped[section]) {
        grouped[section] = [];
      }

      grouped[section].push({
        id: item.id.toString(),
        label: item.title || "제목 없음",
        date: section,
        showDate: false,
        isStorage: true,
        isCompleted: item.is_completed,
      });
    });

    // 각 섹션의 첫 항목에만 날짜 표시
    const result: StorageItem[] = [];
    Object.entries(grouped).forEach(([_, items]) => {
      items.forEach((item, index) => {
        result.push({
          ...item,
          showDate: index === 0,
        });
      });
    });

    return result;
  }, [data]);

  /**
   * 아이템 클릭 - 해당 보관함으로 네비게이션
   */
  const handleItemClick = useCallback(
    (itemId: string) => {
      const navState = {
        from_chat: "storage" as const,
        his_cnt: 0,
        is_writed: false,
        is_completed: false,
      };

      // mode에 따라 라우팅 경로 결정 (현재 pageName이 아닌 mode 기준)
      const basePath = mode === Mode.TUTOR ? "tutor" : "socrates";

      setCurrentGroupId(itemId);
      navigate(`/${basePath}/${itemId}`, { state: navState });
    },
    [navigate, mode, setCurrentGroupId]
  );

  /**
   * 보관함에서 삭제
   */
  const handleItemDelete = useCallback(
    async (itemId: string) => {
      await deleteFromStorageMutation.mutateAsync(itemId);
    },
    [deleteFromStorageMutation]
  );

  /**
   * 무한 스크롤 핸들러
   */
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    // 데이터
    items: storageItems,
    label: mode === Mode.TUTOR ? "튜터 보관함" : "소크라테스 보관함",

    // 상태
    isLoading: isLoading || deleteFromStorageMutation.isPending,
    hasMore: hasNextPage,
    isFetchingMore: isFetchingNextPage,

    // 핸들러
    handleItemClick,
    handleItemDelete,
    handleLoadMore,
  };
};
